
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import ContentSwitcher from '../../../components/docs/ContentSwitcher';
import SampleCode from '../../../components/docs/plaidoms/SampleCode';
import LinkTokenCreate from '../../../components/docs/LinkTokenCreate';
export const metadata = {
  toc: true,
  layout: 'guide',
  description: 'Follow these instructions to add Investments endpoints to your application. Investments allows you to obtain information on securities and holdings.'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const MDXCodeBlock = makeShortcode("MDXCodeBlock");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Add Investments to your app' subtitle='Learn how to add Investments endpoints to your application' mdxType="Header" />
    <p>{`In this guide, we'll start from scratch and walk through how to use `}<a parentName="p" {...{
        "href": "/docs/api/products/investments/"
      }}>{`Investments`}</a>{` to retrieve information on securities and holdings. If you are already familiar with using Plaid and are set up to make calls to the Plaid API, you can skip ahead to `}<a parentName="p" {...{
        "href": "#fetching-investment-data"
      }}>{`Fetching investment data`}</a>{`.`}</p>
    <h4 {...{
      "id": "get-plaid-api-keys-and-complete-application-and-company-profile"
    }}>{`Get Plaid API keys and complete application and company profile`}</h4>
    <p>{`If you don't already have one, you'll need to `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/signup"
      }}>{`create a Plaid developer account`}</a>{`. After creating your account, you can find your `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/developers/keys"
      }}>{`API keys`}</a>{` under Build in the Plaid Dashboard.`}</p>
    <p>{`You will also need to complete your `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/settings/company/app-branding"
      }}>{`application profile`}</a>{` and `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/settings/company/profile"
      }}>{`company profile`}</a>{` on the Dashboard. The information in your profile will be shared with users of your application when they manage their connection on the `}<a parentName="p" {...{
        "href": "https://my.plaid.com"
      }}>{`Plaid Portal`}</a>{`. Your application profile and company profile must be completed before connecting to certain institutions in Production.`}</p>
    <h4 {...{
      "id": "install-and-initialize-plaid-libraries"
    }}>{`Install and initialize Plaid libraries`}</h4>
    <p>{`You can use our official server-side client libraries to connect to the Plaid API from your application:`}</p>
    <SampleCode.InstallClientLibraries />
    <p>{`After you've installed Plaid's client libraries, you can initialize them by passing in your `}<inlineCode parentName="p">{`client_id`}</inlineCode>{`, `}<inlineCode parentName="p">{`secret`}</inlineCode>{`, and the environment you wish to connect to (Sandbox or Production). This will make sure the client libraries pass along your `}<inlineCode parentName="p">{`client_id`}</inlineCode>{` and `}<inlineCode parentName="p">{`secret`}</inlineCode>{` with each request, and you won't need to explicitly include them in any other calls.`}</p>
    <SampleCode.InitializeClientLibraries />
    <h4 {...{
      "id": "create-an-item-in-link"
    }}>{`Create an Item in Link`}</h4>
    <p>{`Plaid Link is a drop-in module that provides a secure, elegant authentication flow
for each institution that Plaid supports. Link makes it secure and easy for users to
connect their bank accounts to Plaid. Note that these instructions cover Link on the web. For instructions on using Link within mobile apps, see the `}<a parentName="p" {...{
        "href": "/docs/link"
      }}>{`Link documentation`}</a>{`.`}</p>
    <p>{`Using Link, we will create a Plaid `}<em parentName="p">{`Item`}</em>{`, which is a Plaid term for a login at a financial institution. An Item is not the same as a financial institution account, although every account will be associated with an Item. For example, if a user has one login at their bank that allows them to access both their checking account and their savings account, a single Item would be associated with both of those accounts. If you want to customize Link's look and feel, you can do so from the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/link"
      }}>{`Dashboard`}</a>{`.`}</p>
    <p>{`Before initializing Link, you will need to create a new `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` on the server side of your application.
A `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` is a short-lived, one-time use token that is used to authenticate your app with Link.
You can create one using the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` endpoint. Then, on the client side of your application, you'll need to initialize Link with the `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` that you just created.`}</p>
    <Callout mdxType="Callout">
      <p>{`You can use `}<a parentName="p" {...{
          "href": "/docs/link/multi-item-link"
        }}>{`Multi-Item Link`}</a>{` to let users link multiple institutions in a single Link session, rather than one at a time across separate sessions.`}</p>
    </Callout>
    <h5 {...{
      "id": "create-a-link_token"
    }}>{`Create a link_token`}</h5>
    <ContentSwitcher.Container id='investments_multi_item' names={['Single institution', 'Multiple institutions (Multi-Item Link)']} hiddenIsSearchable={true}>
      <ContentSwitcher.Content>
        <LinkTokenCreate useCases={[{
          useCase: "Managing a user's investments",
          config: {
            user: {
              client_user_id: 'user-abc',
              email_address: 'user@example.com'
            },
            products: ['investments'],
            client_name: 'Investment Tracker',
            language: 'en',
            country_codes: ['US'],
            webhook: 'https://sample-web-hook.com',
            redirect_uri: 'https://domainname.com/oauth-page.html'
          }
        }]} filenameAsTitle={true} mdxType="LinkTokenCreate" />
      </ContentSwitcher.Content>
      <ContentSwitcher.Content>
        <p>{`Multi-Item Link requires a `}<inlineCode parentName="p">{`user_id`}</inlineCode>{`. Call `}<inlineCode parentName="p">{`/user/create`}</inlineCode>{` first, then pass that `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` (instead of a `}<inlineCode parentName="p">{`user`}</inlineCode>{` object) to `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` along with `}<inlineCode parentName="p">{`enable_multi_item_link: true`}</inlineCode>{`.`}</p>
        <MDXCodeBlock type='server' title='/user/create' mdxType="MDXCodeBlock">
          <pre><code parentName="pre" {...{
              "className": "language-node",
              "metastring": "#server.js",
              "#server.js": true
            }}>{`app.post('/api/create_plaid_user', async function (request, response) {
  // Get the client_user_id by searching for the current user
  const user = await User.find(...);
  const clientUserId = user.id;
  const userResponse = await client.userCreate({
    client_user_id: clientUserId,
  });
  const plaidUserId = userResponse.data.user_id;
  await User.update(user.id, { plaidUserId });
  response.json({ user_id: plaidUserId });
});
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-bash"
            }}>{`curl -X POST https://sandbox.plaid.com/user/create \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "client_user_id": "\${UNIQUE_USER_ID}"
}'
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-ruby",
              "metastring": "#server.rb",
              "#server.rb": true
            }}>{`post '/api/create_plaid_user' do
  current_user = User.find(...)
  client_user_id = current_user.id

  request = Plaid::UserCreateRequest.new(
    { client_user_id: client_user_id }
  )
  response = client.user_create(request)
  plaid_user_id = response.user_id
  current_user.update(plaid_user_id: plaid_user_id)

  content_type :json
  { user_id: plaid_user_id }.to_json
end
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-java",
              "metastring": "#Server.java",
              "#Server.java": true
            }}>{`String clientUserId = userFromDB.id;

UserCreateRequest request = new UserCreateRequest()
  .clientUserId(clientUserId);

UserCreateResponse response = plaidClient
  .userCreate(request, null)
  .execute()
  .body();

String plaidUserId = response.getUserId();
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-python",
              "metastring": "#server.py",
              "#server.py": true
            }}>{`client_user_id = user.id

request = UserCreateRequest(
    client_user_id=client_user_id
)
response = client.user_create(request)
plaid_user_id = response['user_id']
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-go",
              "metastring": "#server.go",
              "#server.go": true
            }}>{`clientUserId := user.ID.String()

request := plaid.NewUserCreateRequest(clientUserId)
resp, _, err := client.PlaidApi.UserCreate(ctx).
  UserCreateRequest(*request).
  Execute()
plaidUserId := resp.GetUserId()
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-go",
              "metastring": "sdk=fern #server.go",
              "sdk": "fern",
              "#server.go": true
            }}>{`clientUserId := user.ID.String()

request := &plaid.UserCreateRequest{
  ClientUserID: clientUserId,
}
resp, err := client.User.Create(ctx, request)
plaidUserId := resp.GetUserID()
`}</code></pre>
        </MDXCodeBlock>
        <MDXCodeBlock type='server' title='/link/token/create' mdxType="MDXCodeBlock">
          <pre><code parentName="pre" {...{
              "className": "language-node",
              "metastring": "#server.js",
              "#server.js": true
            }}>{`app.post('/api/create_link_token', async function (request, response) {
  // plaidUserId was returned by /user/create in the previous step
  const user = await User.find(...);
  const plaidUserId = user.plaidUserId;
  const linkTokenRequest = {
    user_id: plaidUserId,
    enable_multi_item_link: true,
    client_name: 'Plaid Test App',
    products: ['investments'],
    language: 'en',
    webhook: 'https://webhook.example.com',
    redirect_uri: 'https://domainname.com/oauth-page.html',
    country_codes: ['US'],
  };
  try {
    const createTokenResponse = await client.linkTokenCreate(linkTokenRequest);
    response.json(createTokenResponse.data);
  } catch (error) {
    // handle error
  }
});
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-bash"
            }}>{`curl -X POST https://sandbox.plaid.com/link/token/create \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "client_name": "Plaid Test App",
  "user_id": "\${PLAID_USER_ID}",
  "enable_multi_item_link": true,
  "products": ["investments"],
  "country_codes": ["US"],
  "language": "en",
  "webhook": "https://webhook.example.com",
  "redirect_uri": "https://domainname.com/oauth-page.html"
}'
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-ruby",
              "metastring": "#server.rb",
              "#server.rb": true
            }}>{`post '/api/create_link_token' do
  # plaid_user_id was returned by /user/create in the previous step
  plaid_user_id = current_user.plaid_user_id

  request = Plaid::LinkTokenCreateRequest.new(
    {
      user_id: plaid_user_id,
      enable_multi_item_link: true,
      client_name: 'Plaid Test App',
      products: ['investments'],
      country_codes: ['US'],
      language: "en",
      redirect_uri: nil_if_empty_envvar('PLAID_REDIRECT_URI'),
      webhook: 'https://webhook.example.com'
    }
  )
  response = client.link_token_create(request)
  content_type :json
  response.to_json
end
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-java",
              "metastring": "#Server.java",
              "#Server.java": true
            }}>{`// plaidUserId was returned by /user/create in the previous step
String plaidUserId = userFromDB.plaidUserId;

LinkTokenCreateRequest request = new LinkTokenCreateRequest()
  .userId(plaidUserId)
  .enableMultiItemLink(true)
  .clientName("Plaid Test App")
  .products(Arrays.asList(Products.INVESTMENTS))
  .countryCodes(Arrays.asList(CountryCode.US))
  .language("en")
  .redirectUri("https://domainname.com/oauth-page.html")
  .webhook("https://webhook.example.com");

LinkTokenCreateResponse response = plaidClient
  .linkTokenCreate(request)
  .execute()
  .body();
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-python",
              "metastring": "#server.py",
              "#server.py": true
            }}>{`# plaid_user_id was returned by /user/create in the previous step
plaid_user_id = user.plaid_user_id

request = LinkTokenCreateRequest(
        products=[Products('investments')],
        client_name="Plaid Test App",
        country_codes=[CountryCode('US')],
        redirect_uri='https://domainname.com/oauth-page.html',
        language='en',
        webhook='https://webhook.example.com',
        user_id=plaid_user_id,
        enable_multi_item_link=True,
    )
response = client.link_token_create(request)
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-go",
              "metastring": "#server.go",
              "#server.go": true
            }}>{`// plaidUserId was returned by /user/create in the previous step
plaidUserId := user.PlaidUserId

request := plaid.NewLinkTokenCreateRequest(
  "Plaid Test App",
  "en",
  []plaid.CountryCode{plaid.COUNTRYCODE_US},
)
request.SetUserId(plaidUserId)
request.SetEnableMultiItemLink(true)
request.SetWebhook("https://webhook.example.com")
request.SetRedirectUri("https://domainname.com/oauth-page.html")
request.SetProducts([]plaid.Products{plaid.PRODUCTS_INVESTMENTS})

resp, _, err := client.PlaidApi.LinkTokenCreate(ctx).
  LinkTokenCreateRequest(*request).
  Execute()
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-go",
              "metastring": "sdk=fern #server.go",
              "sdk": "fern",
              "#server.go": true
            }}>{`// plaidUserId was returned by /user/create in the previous step
plaidUserId := user.PlaidUserId

request := &plaid.LinkTokenCreateRequest{
  ClientName: "Plaid Test App",
  Language: "en",
  CountryCodes: []plaid.CountryCode{plaid.CountryCodeUs},
  UserID: plaid.String(plaidUserId),
  EnableMultiItemLink: plaid.Bool(true),
  Webhook: plaid.String("https://webhook.example.com"),
  RedirectURI: plaid.String("https://domainname.com/oauth-page.html"),
  Products: []plaid.Products{plaid.ProductsInvestments},
}

resp, err := client.Link.CreateToken(ctx, request)
`}</code></pre>
        </MDXCodeBlock>
      </ContentSwitcher.Content>
    </ContentSwitcher.Container>
    <h5 {...{
      "id": "install-link-dependency"
    }}>{`Install Link dependency`}</h5>
    <pre><code parentName="pre" {...{
        "className": "language-html",
        "metastring": "#index.html",
        "#index.html": true
      }}>{`<head>
  <title>Connect a bank</title>
  <script src="https://cdn.plaid.com/link/v2/stable/link-initialize.js"></script>
</head>
`}</code></pre>
    <h5 {...{
      "id": "configure-the-client-side-link-handler"
    }}>{`Configure the client-side Link handler`}</h5>
    <ContentSwitcher.Container id='investments_multi_item' names={['Single institution', 'Multiple institutions (Multi-Item Link)']} hiddenIsSearchable={true}>
      <ContentSwitcher.Content>
        <pre><code parentName="pre" {...{
            "className": "language-javascript",
            "metastring": "switcher=false #app.js",
            "switcher": "false",
            "#app.js": true
          }}>{`const linkHandler = Plaid.create({
  token: (await $.post('/create_link_token')).link_token,
  onSuccess: (public_token, metadata) => {
    // Send the public_token to your app server.
    $.post('/exchange_public_token', {
      public_token: public_token,
    });
  },
  onExit: (err, metadata) => {
    // Optionally capture when your user exited the Link flow.
    // Storing this information can be helpful for support.
  },
  onEvent: (eventName, metadata) => {
    // Optionally capture Link flow events, streamed through
    // this callback as your users connect an Item to Plaid.
  },
});

linkHandler.open();
`}</code></pre>
      </ContentSwitcher.Content>
      <ContentSwitcher.Content>
        <p>{`With Multi-Item Link, `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` will be called but will not contain public tokens. Instead, get them either from the `}<a parentName="p" {...{
            "href": "/docs/api/link/#session_finished"
          }}><inlineCode parentName="a">{`SESSION_FINISHED`}</inlineCode></a>{` webhook or by calling `}<inlineCode parentName="p">{`/link/token/get`}</inlineCode>{` after `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` fires.`}</p>
        <pre><code parentName="pre" {...{
            "className": "language-javascript",
            "metastring": "switcher=false #app.js",
            "switcher": "false",
            "#app.js": true
          }}>{`const linkHandler = Plaid.create({
  token: (await $.post('/create_link_token')).link_token,
  onSuccess: (public_token, metadata) => {
    // public_token will not be populated here; public_tokens are
    // retrieved via the SESSION_FINISHED webhook or /link/token/get instead.
  },
  onExit: (err, metadata) => {
    // Optionally capture when your user exited the Link flow.
    // Storing this information can be helpful for support.
  },
  onEvent: (eventName, metadata) => {
    // Optionally capture Link flow events, streamed through
    // this callback as your users connect an Item to Plaid.
  },
});

linkHandler.open();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-node",
            "metastring": "switcher=false #webhookServer.js",
            "switcher": "false",
            "#webhookServer.js": true
          }}>{`app.post('/server/receive_webhook', async (req, res, next) => {
  const { webhook_type, webhook_code, status, public_tokens } = req.body;
  const isMultiItemSessionFinished =
    webhook_type === 'LINK' &&
    webhook_code === 'SESSION_FINISHED' &&
    status === 'success';
  if (isMultiItemSessionFinished) {
    // Exchange each token in public_tokens for an access_token
    // via /item/public_token/exchange.
  }
  // Handle other types of webhooks
});
`}</code></pre>
        <p>{`Alternatively, call `}<inlineCode parentName="p">{`/link/token/get`}</inlineCode>{` with the `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` and read the public tokens from `}<inlineCode parentName="p">{`link_sessions[].results.item_add_results[]`}</inlineCode>{` in the response.`}</p>
        <MDXCodeBlock type='server' title='/link/token/get' mdxType="MDXCodeBlock">
          <pre><code parentName="pre" {...{
              "className": "language-node",
              "metastring": "#server.js",
              "#server.js": true
            }}>{`const request = {
  link_token: linkToken,
};
const response = await client.linkTokenGet(request);
const publicTokens = response.data.link_sessions
  .flatMap((session) => session.results.item_add_results)
  .map((result) => result.public_token);
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-bash"
            }}>{`curl -X POST https://sandbox.plaid.com/link/token/get \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "link_token": "\${LINK_TOKEN}"
}'
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-ruby",
              "metastring": "#server.rb",
              "#server.rb": true
            }}>{`request = Plaid::LinkTokenGetRequest.new({ link_token: link_token })
response = client.link_token_get(request)
public_tokens = response.link_sessions.flat_map do |session|
  session.results.item_add_results.map(&:public_token)
end
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-java",
              "metastring": "#Server.java",
              "#Server.java": true
            }}>{`LinkTokenGetRequest request = new LinkTokenGetRequest()
  .linkToken(linkToken);
LinkTokenGetResponse response = plaidClient
  .linkTokenGet(request)
  .execute()
  .body();

List<String> publicTokens = new ArrayList<>();
for (LinkTokenGetSessionsResponse session : response.getLinkSessions()) {
  LinkSessionResults results = session.getResults();
  for (LinkSessionItemAddResult result : results.getItemAddResults()) {
    publicTokens.add(result.getPublicToken());
  }
}
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-python",
              "metastring": "#server.py",
              "#server.py": true
            }}>{`request = LinkTokenGetRequest(link_token=link_token)
response = client.link_token_get(request)
public_tokens = [
    result['public_token']
    for session in response['link_sessions']
    for result in session['results']['item_add_results']
]
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-go",
              "metastring": "#server.go",
              "#server.go": true
            }}>{`linkTokenGetRequest := plaid.NewLinkTokenGetRequest(linkToken)
linkTokenGetResp, _, err := client.PlaidApi.LinkTokenGet(ctx).
  LinkTokenGetRequest(*linkTokenGetRequest).
  Execute()

var publicTokens []string
for _, session := range linkTokenGetResp.GetLinkSessions() {
  results := session.GetResults()
  for _, result := range results.GetItemAddResults() {
    publicTokens = append(publicTokens, result.GetPublicToken())
  }
}
`}</code></pre>
          <pre><code parentName="pre" {...{
              "className": "language-go",
              "metastring": "sdk=fern #server.go",
              "sdk": "fern",
              "#server.go": true
            }}>{`linkTokenGetRequest := &plaid.LinkTokenGetRequest{
  LinkToken: linkToken,
}
linkTokenGetResp, err := client.Link.GetToken(ctx, linkTokenGetRequest)

var publicTokens []string
for _, session := range linkTokenGetResp.GetLinkSessions() {
  results := session.GetResults()
  for _, result := range results.GetItemAddResults() {
    publicTokens = append(publicTokens, result.GetPublicToken())
  }
}
`}</code></pre>
        </MDXCodeBlock>
      </ContentSwitcher.Content>
    </ContentSwitcher.Container>
    <h4 {...{
      "id": "get-a-persistent-access_token"
    }}>{`Get a persistent access_token`}</h4>
    <p>{`Next, on the server side, we need to exchange our `}<inlineCode parentName="p">{`public_token`}</inlineCode>{`(s) for an `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` and `}<inlineCode parentName="p">{`item_id`}</inlineCode>{` for each linked Item. The `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` will allow us to make authenticated calls to the Plaid API. Doing so is as easy as calling the `}<inlineCode parentName="p">{`/item/public_token/exchange`}</inlineCode>{` endpoint from our server-side handler. We'll use the client library we configured earlier to make the API call.`}</p>
    <p>{`Save each `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` and `}<inlineCode parentName="p">{`item_id`}</inlineCode>{` in a secure datastore, as they're used to access Item data and identify webhooks, respectively. The `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` will remain valid unless you actively choose to expire it via rotation or remove the corresponding Item via `}<inlineCode parentName="p">{`/item/remove`}</inlineCode>{`. The `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` should be stored securely, and never in client-side code. A `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` is a one-time use token with a lifetime of 30 minutes, so there is no need to store it.`}</p>
    <SampleCode.ExchangePublicToken />
    <p>{`If you used Multi-Item Link, call `}<inlineCode parentName="p">{`/item/public_token/exchange`}</inlineCode>{` once per token in your `}<inlineCode parentName="p">{`public_tokens`}</inlineCode>{` array, saving the resulting `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` and `}<inlineCode parentName="p">{`item_id`}</inlineCode>{` pair each time.`}</p>
    <h4 {...{
      "id": "fetching-investment-data"
    }}>{`Fetching investment data`}</h4>
    <p>{`Now that the authentication step is out of the way, we can begin using authenticated endpoints from the Plaid API. Once you've retrieved investment data for an account, you can then use it to analyze data such as trading activity, net worth, and asset allocations.`}</p>
    <p>{`Investments endpoints return two primary pieces of information about the investment account. The first is details on the holding itself (if using `}<inlineCode parentName="p">{`/investments/holdings/get`}</inlineCode>{`) or the transaction (if using `}<inlineCode parentName="p">{`/investments/transactions/get`}</inlineCode>{`). The second is details about the security itself. Each security, transaction, and holding contains a `}<inlineCode parentName="p">{`security_id`}</inlineCode>{` field, which functions as a key for cross-referencing the holding or transaction with the security it pertains to. For more detailed information on the schema for information returned, see `}<inlineCode parentName="p">{`/investments/holdings/get`}</inlineCode>{` or `}<inlineCode parentName="p">{`/investments/transactions/get`}</inlineCode>{`.`}</p>
    <p>{`Investments data is updated at least once per day during market days (up to 2-4 times per day, depending on institution). Holdings most commonly change after market close, when prices settle. To be alerted when new data is available to be retrieved, listen to `}<a parentName="p" {...{
        "href": "/docs/api/products/investments/#webhooks"
      }}>{`Investments webhooks`}</a>{`.`}</p>
    <h5 {...{
      "id": "fetching-investment-holdings"
    }}>{`Fetching investment holdings`}</h5>
    <MDXCodeBlock title='/investments/holdings/get' mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`import (
    "context"

    "github.com/plaid/plaid-go/v48/plaid"
)

holdingsGetReq := plaid.NewInvestmentsHoldingsGetRequest("ACCESS_TOKEN")
holdingsGetReqOptions := plaid.NewInvestmentHoldingsGetRequestOptions()
holdingsGetReqOptions.SetAccountIds([]string{"ACCOUNT_ID"})
holdingsGetReq.SetOptions(*holdingsGetReqOptions)

holdingsGetResp, _, err := client.PlaidApi.InvestmentsHoldingsGet(ctx).InvestmentsHoldingsGetRequest(
  *holdingsGetReq,
).Execute()

securities := holdingsGetResp.GetSecurities()
holdings := holdingsGetResp.GetHoldings()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "sdk=fern",
          "sdk": "fern"
        }}>{`import (
    "context"

    "github.com/plaid/plaid-go/v49"
)

holdingsGetReq := &plaid.InvestmentsHoldingsGetRequest{AccessToken: "ACCESS_TOKEN"}
holdingsGetReqOptions := &plaid.InvestmentHoldingsGetRequestOptions{}
holdingsGetReqOptions.AccountIDs = []string{"ACCOUNT_ID"}
holdingsGetReq.Options = holdingsGetReqOptions

holdingsGetResp, err := client.Investments.GetHoldings(ctx, holdingsGetReq)

securities := holdingsGetResp.GetSecurities()
holdings := holdingsGetResp.GetHoldings()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`const { InvestmentsHoldingsGetRequest } = require('plaid');

// Pull Holdings for an Item
const request: InvestmentsHoldingsGetRequest = {
  access_token: accessToken,
};
try {
  const response = await plaidClient.investmentsHoldingsGet(request);
  const holdings = response.data.holdings;
  const securities = response.data.securities;
} catch (error) {
  // handle error
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`import plaid
from plaid.model.investments_holdings_get_request import InvestmentsHoldingsGetRequest

# Pull Holdings for an Item
request = InvestmentsHoldingsGetRequest(access_token=access_token)
response = client.investments_holdings_get(request)

# Handle Holdings response
holdings = response['holdings']

# Handle Securities response
securities = response['securities']
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-ruby"
        }}>{`require 'plaid'

# Pull Holdings for an Item
request = Plaid::InvestmentsHoldingsGetRequest.new({ access_token: access_token })
response = client.investments_holdings_get(request)

# Handle Holdings response
holdings = response.holdings

# Handle Securities response
securities = response.securities
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`import com.plaid.client.model.InvestmentsHoldingsGetRequest;
import com.plaid.client.model.InvestmentsHoldingsGetResponse;

// Pull Holdings for an Item
InvestmentsHoldingsGetRequest request = new InvestmentsHoldingsGetRequest()
  .accessToken(accessToken);

Response<InvestmentsHoldingsGetResponse> response = client()
  .investmentsHoldingsGet(request)
  .execute();

// Handle Holdings response
List<Holding> holdings =
  response.body().getHoldings();

// Handle Securities response
List<Security> securities =
  response.body().getSecurities();
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`curl -X POST https://sandbox.plaid.com/investments/holdings/get \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "access_token": string
}'
`}</code></pre>
    </MDXCodeBlock>
    <p>{`Example response data is below.`}</p>
    {/* prettier-ignore */}
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Sample investments holdings response",
        "#Sample": true,
        "investments": true,
        "holdings": true,
        "response": true
      }}>{`{
  "accounts": [
    {
      "account_id": "5Bvpj4QknlhVWk7GygpwfVKdd133GoCxB814g",
      "balances": {
        "available": 43200,
        "current": 43200,
        "iso_currency_code": "USD",
        "limit": null,
        "margin_loan_amount": null,
        "unofficial_currency_code": null
      },
      "mask": "4444",
      "name": "Plaid Money Market",
      "official_name": "Plaid Platinum Standard 1.85% Interest Money Market",
      "subtype": "money market",
      "type": "depository"
    },
    {
      "account_id": "JqMLm4rJwpF6gMPJwBqdh9ZjjPvvpDcb7kDK1",
      "balances": {
        "available": null,
        "current": 320.76,
        "iso_currency_code": "USD",
        "limit": null,
        "margin_loan_amount": null,
        "unofficial_currency_code": null
      },
      "mask": "5555",
      "name": "Plaid IRA",
      "official_name": null,
      "subtype": "ira",
      "type": "investment"
    },
    {
      "account_id": "k67E4xKvMlhmleEa4pg9hlwGGNnnEeixPolGm",
      "balances": {
        "available": null,
        "current": 23631.9805,
        "iso_currency_code": "USD",
        "limit": null,
        "margin_loan_amount": null,
        "unofficial_currency_code": null
      },
      "mask": "6666",
      "name": "Plaid 401k",
      "official_name": null,
      "subtype": "401k",
      "type": "investment"
    }
  ],
  "holdings": [
    {
      "account_id": "JqMLm4rJwpF6gMPJwBqdh9ZjjPvvpDcb7kDK1",
      "cost_basis": 0.01,
      "institution_price": 0.011,
      "institution_price_as_of": null,
      "institution_price_datetime": null,
      "institution_value": 110,
      "iso_currency_code": "USD",
      "quantity": 10000,
      "vested_quantity": null,
      "vested_value": null,
      "security_id": "8E4L9XLl6MudjEpwPAAgivmdZRdBPJuvMPlPb",
      "unofficial_currency_code": null,
      "tax_lots": []
    },
    {
      "account_id": "k67E4xKvMlhmleEa4pg9hlwGGNnnEeixPolGm",
      "cost_basis": 23,
      "institution_price": 27,
      "institution_price_as_of": null,
      "institution_price_datetime": null,
      "institution_value": 636.309,
      "iso_currency_code": "USD",
      "quantity": 23.567,
      "vested_quantity": null,
      "vested_value": null,
      "security_id": "JDdP7XPMklt5vwPmDN45t3KAoWAPmjtpaW7DP",
      "unofficial_currency_code": null,
      "tax_lots": [
        {
          "institution_lot_id": "1",
          "original_purchase_datetime": "2022-09-12T00:00:00Z",
          "quantity": 10,
          "purchase_price": 0.98,
          "cost_basis": 9.80,
          "current_value": 270,
          "position_type": "LONG"
        },
        {
          "institution_lot_id": "2",
          "original_purchase_datetime": "2023-02-08T00:00:00Z",
          "quantity": 13.567,
          "purchase_price": 0.973,
          "cost_basis": 13.20,
          "current_value": 366.309,
          "position_type": "LONG"
        }
      ]
    }
  ],
  "item": {
    "available_products": [
      "balance",
      "credit_details",
      "identity",
      "liabilities",
      "transactions"
    ],
    "billed_products": ["assets", "auth", "investments"],
    "consent_expiration_time": null,
    "error": null,
    "institution_id": "ins_3",
    "item_id": "4z9LPae1nRHWy8pvg9jrsgbRP4ZNQvIdbLq7g",
    "webhook": "https://www.genericwebhookurl.com/webhook"
  },
  "request_id": "l68wb8zpS0hqmsJ",
  "securities": [
    {
      "close_price": 0.011,
      "close_price_as_of": null,
      "cusip": null,
      "institution_id": null,
      "institution_security_id": null,
      "is_cash_equivalent": false,
      "isin": null,
      "iso_currency_code": "USD",
      "name": "Nflx Feb 01'18 $355 Call",
      "proxy_security_id": null,
      "security_id": "8E4L9XLl6MudjEpwPAAgivmdZRdBPJuvMPlPb",
      "sedol": null,
      "ticker_symbol": "NFLX180201C00355000",
      "type": "derivative",
      "subtype": "option",
      "unofficial_currency_code": null,
      "update_datetime": null,
      "sector": "Technology Services",
      "industry": "Internet Software or Services",
      "market_identifier_code": "XNAS",
      "cfi_code": "OCASPS",
      "figi": "BBG000B9XRY4",
      "option_contract": {
        "contract_type": "call",
        "expiration_date": "2018-02-01",
        "strike_price": 355.00,
        "underlying_security_ticker": "NFLX"
      },
      "fixed_income": null
    },
    {
      "close_price": 27,
      "close_price_as_of": null,
      "cusip": "577130834",
      "institution_id": null,
      "institution_security_id": null,
      "is_cash_equivalent": false,
      "isin": "US5771308344",
      "iso_currency_code": "USD",
      "name": "Matthews Pacific Tiger Fund Insti Class",
      "proxy_security_id": null,
      "security_id": "JDdP7XPMklt5vwPmDN45t3KAoWAPmjtpaW7DP",
      "sedol": null,
      "ticker_symbol": "MIPTX",
      "type": "mutual fund",
      "subtype": "mutual fund",
      "unofficial_currency_code": null,
      "update_datetime": null,
      "sector": "Miscellaneous",
      "industry": "Investment Trusts or Mutual Funds",
      "market_identifier_code": "XNAS",
      "cfi_code": "CIOIBS",
      "figi": "BBG000BHVLF8",
      "option_contract": null,
      "fixed_income": null
    }
  ]
}
`}</code></pre>
    <h5 {...{
      "id": "fetching-investment-transactions"
    }}>{`Fetching investment transactions`}</h5>
    <MDXCodeBlock type='server' title='Investments transactions sample request' mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`curl -X POST https://sandbox.plaid.com/investments/transactions/get \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "access_token": string,
  "start_date": "2025-03-01",
  "end_date": "2025-04-30",
  "options": {
    "count": 250,
    "offset": 100
  }
}'
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-ruby",
          "metastring": "#server.rb",
          "#server.rb": true
        }}>{`# Pull investment transactions for a date range
investments_transactions_get_request = Plaid::InvestmentsTransactionsGetRequest.new
investments_transactions_get_request.access_token = access_token
investments_transactions_get_request.start_date = '2025-03-01'
investments_transactions_get_request.end_date = '2025-04-30'

response = client.investments_transactions_get(investments_transactions_get_request)
investment_transactions = response.investment_transactions
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java",
          "metastring": "#Server.java",
          "#Server.java": true
        }}>{`LocalDate startDate = LocalDate.parse("2025-03-01");
LocalDate endDate = LocalDate.parse("2025-04-30");

// Pull transactions for a date range
InvestmentsTransactionsGetRequestOptions options = new InvestmentsTransactionsGetRequestOptions()
    .count(100)
    .offset(1);

InvestmentsTransactionsGetRequest request = new InvestmentsTransactionsGetRequest()
  .accessToken(accessToken)
  .startDate(startDate)
  .endDate(endDate)
  .options(options);

Response<InvestmentsTransactionsGetResponse> response = plaidClient
  .investmentsTransactionsGet(request)
  .execute();

for (InvestmentTransaction txn :
  response.body().getInvestmentTransactions()) { ... }
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-node",
          "metastring": "#server.js",
          "#server.js": true
        }}>{`const request: InvestmentsTransactionsGetRequest = {
  access_token: accessToken,
  start_date: '2025-01-01',
  end_date: '2025-06-10',
  options: {
    count: 250,
    offset: 0,
  },
};
try {
  const response = await plaidClient.investmentsTransactionsGet(request);
  const investmentTransactions = response.data.investment_transactions;
} catch (error) {
  // handle error
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python",
          "metastring": "#server.py",
          "#server.py": true
        }}>{`request = InvestmentsTransactionsGetRequest(
    access_token=access_token,
    start_date=datetime.date(2025, 3, 1),
    end_date=datetime.date(2025, 4, 30),
)
response = client.investments_transactions_get(request)
investment_transactions = response['investment_transactions']

# Manipulate the count and offset parameters to paginate
# transactions and retrieve all available data
while len(investment_transactions) < response['total_investment_transactions']:
    options = InvestmentsTransactionsGetRequestOptions(offset=len(investment_transactions))
    request = InvestmentsTransactionsGetRequest(
        access_token=access_token,
        start_date=datetime.date(2025, 3, 1),
        end_date=datetime.date(2025, 4, 30),
        options=options
    )
    response = client.investments_transactions_get(request)
    investment_transactions.extend(response['investment_transactions'])
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "#server.go",
          "#server.go": true
        }}>{`request := plaid.NewInvestmentsTransactionsGetRequest(accessToken, startDateString, endDateString)
response, _, err := client.PlaidApi.InvestmentsTransactionsGet(ctx).InvestmentsTransactionsGetRequest(*request).Execute()

investmentTransactions := response.GetInvestmentTransactions()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "sdk=fern #server.go",
          "sdk": "fern",
          "#server.go": true
        }}>{`import (
    "time"
)

startDate, err := time.Parse("2006-01-02", startDateString)
if err != nil {
    return err
}
endDate, err := time.Parse("2006-01-02", endDateString)
if err != nil {
    return err
}
request := &plaid.InvestmentsTransactionsGetRequest{
    AccessToken: accessToken,
    StartDate:   startDate,
    EndDate:     endDate,
}
response, err := client.Investments.GetTransactions(ctx, request)

var investmentTransactions []*plaid.InvestmentTransaction
if response != nil {
    investmentTransactions = response.Response.GetInvestmentTransactions()
}
`}</code></pre>
    </MDXCodeBlock>
  
    <p>{`Sample response data is below.`}</p>
    {/* prettier-ignore */}
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Investments transactions sample response",
        "#Investments": true,
        "transactions": true,
        "sample": true,
        "response": true
      }}>{`{
  "accounts": [
    {
      "account_id": "5e66Dl6jNatx3nXPGwZ7UkJed4z6KBcZA4Rbe",
      "balances": {
        "available": 100,
        "current": 110,
        "iso_currency_code": "USD",
        "limit": null,
        "margin_loan_amount": null,
        "unofficial_currency_code": null
      },
      "mask": "0000",
      "name": "Plaid Checking",
      "official_name": "Plaid Gold Standard 0% Interest Checking",
      "subtype": "checking",
      "type": "depository"
    },
    {
      "account_id": "KqZZMoZmBWHJlz7yKaZjHZb78VNpaxfVa7e5z",
      "balances": {
        "available": null,
        "current": 320.76,
        "iso_currency_code": "USD",
        "limit": null,
        "margin_loan_amount": null,
        "unofficial_currency_code": null
      },
      "mask": "5555",
      "name": "Plaid IRA",
      "official_name": null,
      "subtype": "ira",
      "type": "investment"
    },
    {
      "account_id": "rz99ex9ZQotvnjXdgQLEsR81e3ArPgulVWjGj",
      "balances": {
        "available": null,
        "current": 23631.9805,
        "iso_currency_code": "USD",
        "limit": null,
        "margin_loan_amount": null,
        "unofficial_currency_code": null
      },
      "mask": "6666",
      "name": "Plaid 401k",
      "official_name": null,
      "subtype": "401k",
      "type": "investment"
    }
  ],
  "investment_transactions": [
    {
      "account_id": "rz99ex9ZQotvnjXdgQLEsR81e3ArPgulVWjGj",
      "amount": -8.72,
      "cancel_transaction_id": null,
      "date": "2020-05-29",
      "fees": 0,
      "investment_transaction_id": "oq99Pz97joHQem4BNjXECev1E4B6L6sRzwANW",
      "iso_currency_code": "USD",
      "name": "INCOME DIV DIVIDEND RECEIVED",
      "price": 0,
      "quantity": 0,
      "security_id": "NDVQrXQoqzt5v3bAe8qRt4A7mK7wvZCLEBBJk",
      "subtype": "dividend",
      "type": "cash",
      "unofficial_currency_code": null
    },
    {
      "account_id": "rz99ex9ZQotvnjXdgQLEsR81e3ArPgulVWjGj",
      "amount": -1289.01,
      "cancel_transaction_id": null,
      "date": "2020-05-28",
      "fees": 7.99,
      "investment_transaction_id": "pK99jB9e7mtwjA435GpVuMvmWQKVbVFLWme57",
      "iso_currency_code": "USD",
      "name": "SELL Matthews Pacific Tiger Fund Insti Class",
      "price": 27,
      "quantity": -47.74104242992852,
      "security_id": "JDdP7XPMklt5vwPmDN45t3KAoWAPmjtpaW7DP",
      "subtype": "sell",
      "type": "sell",
      "unofficial_currency_code": null
    },
    {
      "account_id": "rz99ex9ZQotvnjXdgQLEsR81e3ArPgulVWjGj",
      "amount": 7.70,
      "cancel_transaction_id": null,
      "date": "2020-05-27",
      "fees": 7.99,
      "investment_transaction_id": "LKoo1ko93wtreBwM7yQnuQ3P5DNKbKSPRzBNv",
      "iso_currency_code": "USD",
      "name": "BUY DoubleLine Total Return Bond Fund",
      "price": 10.42,
      "quantity": 0.7388014749727547,
      "security_id": "NDVQrXQoqzt5v3bAe8qRt4A7mK7wvZCLEBBJk",
      "subtype": "buy",
      "type": "buy",
      "unofficial_currency_code": null
    }
  ],
  "item": {
    "available_products": ["assets", "balance", "identity", "transactions"],
    "billed_products": ["auth", "investments"],
    "consent_expiration_time": null,
    "error": null,
    "institution_id": "ins_12",
    "item_id": "8Mqq5rqQ7Pcxq9MGDv3JULZ6yzZDLMCwoxGDq",
    "webhook": "https://www.genericwebhookurl.com/webhook"
  },
  "request_id": "iv4q3ZlytOOthkv",
  "securities": [
    {
      "close_price": 27,
      "close_price_as_of": null,
      "cusip": "577130834",
      "institution_id": null,
      "institution_security_id": null,
      "is_cash_equivalent": false,
      "isin": "US5771308344",
      "iso_currency_code": "USD",
      "name": "Matthews Pacific Tiger Fund Insti Class",
      "proxy_security_id": null,
      "security_id": "JDdP7XPMklt5vwPmDN45t3KAoWAPmjtpaW7DP",
      "sedol": null,
      "ticker_symbol": "MIPTX",
      "type": "mutual fund",
      "subtype": "mutual fund",
      "unofficial_currency_code": null,
      "update_datetime": null,
      "sector": "Miscellaneous",
      "industry": "Investment Trusts or Mutual Funds",
      "market_identifier_code": "XNAS",
      "cfi_code": "CIOIBS",
      "figi": "BBG000BHVLF8",
      "option_contract": null,
      "fixed_income": null
    },
    {
      "close_price": 10.42,
      "close_price_as_of": null,
      "cusip": "258620103",
      "institution_id": null,
      "institution_security_id": null,
      "is_cash_equivalent": false,
      "isin": "US2586201038",
      "iso_currency_code": "USD",
      "name": "DoubleLine Total Return Bond Fund",
      "proxy_security_id": null,
      "security_id": "NDVQrXQoqzt5v3bAe8qRt4A7mK7wvZCLEBBJk",
      "sedol": null,
      "ticker_symbol": "DBLTX",
      "type": "mutual fund",
      "subtype": "mutual fund",
      "unofficial_currency_code": null,
      "update_datetime": null,
      "sector": "Miscellaneous",
      "industry": "Investment Trusts or Mutual Funds",
      "market_identifier_code": "XNAS",
      "cfi_code": "CIOIBS",
      "figi": "BBG000JBSV40",
      "option_contract": null,
      "fixed_income": null
    }
  ],
  "total_investment_transactions": 3
}
`}</code></pre>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "get-plaid-api-keys-and-complete-application-and-company-profile",
        level: 4,
        title: "Get Plaid API keys and complete application and company profile",
      },
{
        id: "install-and-initialize-plaid-libraries",
        level: 4,
        title: "Install and initialize Plaid libraries",
      },
{
        id: "create-an-item-in-link",
        level: 4,
        title: "Create an Item in Link",
      },
{
        id: "get-a-persistent-access_token",
        level: 4,
        title: "Get a persistent access_token",
      },
{
        id: "fetching-investment-data",
        level: 4,
        title: "Fetching investment data",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
